import { useMemo } from 'react';

import {
	usePsfSharePageData,
	usePsfSharePageFcm,
	usePsfSharePageFcmExperiments,
} from 'src/app/pages/psf/share/pageContext';
import { SHARE_CHANNELS } from 'src/app/pages/psf/share/shared/consts';
import type { ShareChannel } from 'src/app/pages/psf/share/shared/types';
import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useIsMobile } from 'src/app/shared/hooks/userAgent';

type OldShareChannelName = Exclude<
	ReturnType<typeof usePsfSharePageFcm>['shareButtonsListNonInitial'][0],
	ShareChannel
>;
type OldShareChannelNameFromQuery = 'copylink' | 'facebook_message' | 'instagram_story';
const OLD_TO_NEW_SHARE_CHANNEL_MAPPING: Record<string, ShareChannel> = {
	copylink: 'copy',
	facebook_message: 'facebookMessenger',
	instagram_story: 'instagram',
} satisfies Record<OldShareChannelName | OldShareChannelNameFromQuery, ShareChannel>;

function mapOldShareChannels(channels: ReadonlyArray<OldShareChannelName | ShareChannel>): readonly ShareChannel[] {
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	return channels.map((name) => OLD_TO_NEW_SHARE_CHANNEL_MAPPING[name] || name);
}

export function useShareChannels(): readonly ShareChannel[] {
	const buttons = useQueryParamValue('buttons');
	const {
		data: { initial },
	} = usePsfSharePageData();
	const { shareButtonsListInitial, shareButtonsListNonInitial } = usePsfSharePageFcm();
	const {
		data: {
			petition: { geoReach },
		},
	} = usePsfSharePageData();
	const isMobile = useIsMobile();

	const {
		redditExperiment: { variation },
	} = usePsfSharePageFcmExperiments();

	return useMemo(() => {
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unsafe-type-assertion
		const fromQuery = (buttons ? buttons.split(',') : []).filter(
			(name) => SHARE_CHANNELS[name],
		) as readonly ShareChannel[];
		const fromFc = initial ? shareButtonsListInitial : mapOldShareChannels(shareButtonsListNonInitial);

		const shareButtonsList = fromQuery.length ? fromQuery : fromFc;

		const treatedShareButtonList =
			variation !== 'reddit' ? shareButtonsList.filter((x) => x !== 'reddit') : shareButtonsList;

		const filteredShareButtonsList = !isMobile
			? treatedShareButtonList.filter((ch) => ch !== 'mobileNativeShare')
			: treatedShareButtonList;

		if (filteredShareButtonsList.includes('nextdoor') && geoReach !== 'local') {
			return filteredShareButtonsList.filter((ch) => ch !== 'nextdoor');
		}

		return filteredShareButtonsList;
	}, [buttons, initial, shareButtonsListInitial, shareButtonsListNonInitial, geoReach, isMobile, variation]);
}
